import React, { ReactElement, useCallback, useState } from 'react';
import Modal from '@components/Modal';
import UserWorkflowDocumentsSelectorForm from '@/lib/UserWorkflowDocumentsSelectorForm';
import { SelectCompiledDocument } from '@/lib/UserWorkflowDocumentsSelectorForm/UserWorkflowDocumentsSelectorForm';
import * as tsClient from '@tsClient';
import XCircle from '@components/Icons/XCircle';
import ArrowCircleLeft from '@components/Icons/ArrowCircleLeft';
import useNoodleApi from '@/hooks/useNoodleApi';
import ProgressIndicator from '@/components/ProgressIndicator';
import Spacer from '@/components/Spacer';
import removeNullish from '@/helpers/removeNullish';
import CustomSelect from '@/components/FormFields/CustomSelect';
import { UserWorkflowDocument, UserWorkflowDocumentIntegrationFileNames } from '@/typings/api-models';
import Button from '@/components/DesignLibrary/Button';
import TextInput from '@/components/FormFields/TextInput';

type Props = {
  onClose: () => void;
  onSubmit: () => void;
  creatorId: string;
  userWorkflowId: string;
};

const IntegrationFileNameOptions: { value: NonNullable<UserWorkflowDocument['integrationFileName'] | 'other'>; title: string }[] = [
  { title: 'Other', value: 'other' },
  { title: 'petition.pdf', value: 'petition.pdf' },
  { title: 'debtor.txt', value: 'debtor.txt' },
  { title: 'creditor.txt', value: 'creditor.txt' },
  { title: 'credit.pdf', value: 'credit.pdf' },
];

const SelectUserWorkflowDocumentForPacerModal = ({ onClose, onSubmit, creatorId, userWorkflowId }: Props): ReactElement => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedCompiledDocuments, setSelectCompiledDocuments] = useState<SelectCompiledDocument[] | null>(null);
  const [usedFileNames, setUsedFileNames] = useState<UserWorkflowDocumentIntegrationFileNames[]>([]);
  const [inputtedFileName, setInputtedFileName] = useState<string | null>(null);
  const [fileNameError, setFileNameError] = useState<string | null>(null);
  const [isTouched, setIsTouched] = useState(false);
  const [selectedIntegrationFileName, setSelectedIntegrationFileName] = useState<NonNullable<UserWorkflowDocument['integrationFileName'] | 'other'>>('other');
  const { getData: updateUserWorkflowDocuments } = useNoodleApi(tsClient.workflows.updateUserWorkflowCompiledDocuments, {
    toastOnError: true,
  });

  const availableFileNameOptions = IntegrationFileNameOptions.filter(
    option => option.value === 'other' || !usedFileNames.includes(option.value),
  );

  const getIcon = (): ReactElement => {
    if (selectedCompiledDocuments) {
      return <ArrowCircleLeft weight="fill" size={24} color="var(--color-gray-100)" />;
    }
    return <XCircle weight="fill" size={24} color="var(--color-gray-100)" />;
  };

  const generateFinalIntegrationFileName = (
    selectedDoc: SelectCompiledDocument | undefined,
    currentIntegrationFileName: string,
    currentInputtedFileName: string | null,
  ): string | null => {
    if (currentIntegrationFileName !== 'other') {
      return currentIntegrationFileName;
    }

    if (!currentInputtedFileName || !currentInputtedFileName.trim() || !selectedDoc?.document?.mimeType) {
      return null;
    }

    let extension = 'bin'; // fallback extension
    if (selectedDoc.document.mimeType.includes('pdf')) {
      extension = 'pdf';
    } else if (selectedDoc.document.mimeType.includes('text')) {
      extension = 'txt';
    }

    const normalized = currentInputtedFileName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

    return `${normalized}.${extension}`;
  };

  const handleAddDocumentsToPacer = useCallback(async (
    updatedDocs: SelectCompiledDocument[],
    currentlySelectedIntegrationFileName: string,
    currentlySelectedInputtedFileName: string | null,
  ): Promise<void> => {
    if (!updatedDocs) return;
    const updates = updatedDocs.map((doc, index) => ({
      ...doc,
      rank: index + 1,
    }))
      .map((doc, index) => {
        if (doc.isSelected && doc.document) {
          const integrationFileName = generateFinalIntegrationFileName(doc, currentlySelectedIntegrationFileName, currentlySelectedInputtedFileName);
          return {
            documentId: doc.document.id,
            id: doc.id,
            integrationFileName: integrationFileName as UserWorkflowDocumentIntegrationFileNames,
            integrationSlug: 'pacer' as const,
            rank: index + 1,
            title: doc.title,
          };
        }
        if (doc.isSelected && !doc.document) {
          return {
            id: doc.id,
            integrationSlug: 'pacer' as const,
            rank: index + 1,
            title: doc.title,
          };
        }
        if (doc.document) {
          return {
            documentId: doc.document.id,
            id: doc.id,
            rank: index + 1,
            title: doc.title,
          };
        }
        return {
          id: doc.id,
          rank: index + 1,
          title: doc.title,
        };
      })
      .filter(removeNullish);

    await updateUserWorkflowDocuments({
      updates,
      userWorkflowId,
    });
  }, [updateUserWorkflowDocuments, userWorkflowId]);

  const handleClose = (): void => {
    if (selectedCompiledDocuments) {
      setSelectCompiledDocuments(null);
      setUsedFileNames([]);
    } else {
      onClose();
    }
  };

  const handleDocumentSelect = async (compiledDocuments: SelectCompiledDocument[]): Promise<void> => {
    setSelectCompiledDocuments(compiledDocuments);

    const currentUsedFileNames = compiledDocuments
      .filter(doc => doc.integrationSlug && doc.integrationFileName)
      .map(doc => doc.integrationFileName).filter(removeNullish);
    setUsedFileNames(currentUsedFileNames);
  };

  const handleFinalSubmit = async (): Promise<void> => {
    if (selectedIntegrationFileName === 'other' && !inputtedFileName) {
      setIsTouched(true);
      return;
    }
    setIsGenerating(true);
    if (selectedCompiledDocuments) {
      await handleAddDocumentsToPacer(selectedCompiledDocuments, selectedIntegrationFileName, inputtedFileName);
    }
    onSubmit();
  };

  let modalContent: React.ReactNode;

  if (isGenerating) {
    modalContent = (
      <>
        <Spacer size={80} />
        <ProgressIndicator isCentered />
        <Spacer />
        <p className='caption' style={{ color: 'var(--color-gray-75)', textAlign: 'center' }}>
          Adding document...
        </p>
        <Spacer size={80} />
      </>
    );
  } else if (selectedCompiledDocuments) {
    modalContent = (
      <div>
        <CustomSelect
          options={availableFileNameOptions}
          value={selectedIntegrationFileName}
          id="pacer-integration-file-name"
          onChange={value => setSelectedIntegrationFileName(value as NonNullable<UserWorkflowDocument['integrationFileName']>)}
          label="Pacer integration file name (some file names might not appear if another Pacer file is already using that name)"
        />
        <Spacer size={16}/>
        {selectedIntegrationFileName === 'other' && (
          <>
            <TextInput
              id={'fileName'}
              value={inputtedFileName || ''}
              onChange={setInputtedFileName}
              label={'File Name'}
              placeholder={`Enter file name`}
              error={fileNameError}
              onError={setFileNameError}
              isTouched={isTouched}
              required={selectedIntegrationFileName === 'other'}
            />
            <Spacer size={12}/>
          </>
        )}
        <Button
          variant='primary'
          size='md'
          fullWidth
          onClick={handleFinalSubmit}
          disabled={!inputtedFileName && selectedIntegrationFileName === 'other'}
        >
          Add document to Pacer files
        </Button>
      </div>
    );
  } else {
    modalContent = (
      <UserWorkflowDocumentsSelectorForm
        creatorId={creatorId}
        isForPetitionPdfGeneration={false}
        userWorkflowId={userWorkflowId}
        onSubmit={handleDocumentSelect}
      />
    );
  }

  return (
    <Modal hasPadding onClose={handleClose} closeIcon={getIcon()} title={`${selectedCompiledDocuments ? 'Mark as...' : 'Select document to add'}`}>
      {modalContent}
    </Modal>
  );
};

export default SelectUserWorkflowDocumentForPacerModal;
